// Shared timezone-aware time formatter (telemetry-uat2 ADR — the Time Standard).
//
// STORAGE IS UTC EVERYWHERE (unchanged). This module renders INSTANTS in the
// viewer's timezone and labels every clock-bearing render with its short zone
// name (e.g. `08:57 AEST`); the raw UTC instant is revealed on hover (the
// `<time>` element's title). The effective zone is the device zone
// (`Intl.DateTimeFormat().resolvedOptions().timeZone`), overridden by the
// signed-in user's profile Timezone preference when set.
//
// THE ONE PLACE THE OPS CONSOLE FORMATS A DATE OR A TIME (Jeff, 27 October 2026:
// "All dates MUST be in the user's timezone if set in their profile (they may be
// travelling), otherwise their device timezone" — brought to the staff portal on
// 28 October 2026). A staff member sees every day and clock in their My Profile
// Timezone, else the device's zone. Every day, clock, month label and
// calendar-day comparison goes through the helpers below; a source-scan test
// (test/date-format-single-source.test.ts) fails CI if a direct `toLocale*` /
// `Intl.DateTimeFormat` / device-local `Date` getter reappears anywhere else in
// portal-staff/src.
//
// Two kinds of value, never confused:
//   - INSTANTS (`…Z`, `…+09:30`, epoch) — a real moment. Its day and clock are
//     read IN THE VIEWER'S ZONE.
//   - CIVIL DATES (`YYYY-MM-DD` — renewal anniversary, billing period, a staff-
//     picked reply-by day) — a business day with no zone. Rendered exactly as
//     stored, in every zone.
// `formatDate` accepts either and tells them apart by shape.
//
// Copied-per-surface per the Common Catalogue model (Structure Guide §9): a fork
// of portal-subscriber/src/lib/time-format.tsx (28 October 2026). The staff copy
// keeps one extra instant mode, `time-sec`, that ops tables use. Never import
// across the portal boundary.

import { useSyncExternalStore } from 'react';

// ── Effective-zone preference store ─────────────────────────────────────────
// A module singleton so the profile page can set the preference and EVERY
// rendered instant re-renders (via useSyncExternalStore) without threading a
// context through the tree. `null` ⇒ use the device zone.
let currentPref: string | null = null;
const listeners = new Set<() => void>();

/** Set the viewer's timezone preference (IANA zone, or null for device zone).
 * Called on auth-load with the stored preference and on a profile Save. */
export function setTzPreference(pref: string | null): void {
  const next = pref && pref.trim() ? pref.trim() : null;
  if (next === currentPref) return;
  currentPref = next;
  listeners.forEach((l) => l());
}

export function getTzPreference(): string | null {
  return currentPref;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** React hook: the stored timezone preference (null ⇒ device zone). */
export function useTzPreference(): string | null {
  return useSyncExternalStore(subscribe, getTzPreference, getTzPreference);
}

// ── Timezone picker options (My Profile) ────────────────────────────────────
// Common AU/NZ zones surfaced first (the platform's primary market), then a
// short global set. '' is the "Device timezone" default (store null). IANA ids.
export interface TimeZoneOption { value: string; label: string; }
export const COMMON_TIME_ZONES: TimeZoneOption[] = [
  { value: '', label: 'Device timezone (automatic)' },
  { value: 'Australia/Sydney', label: 'Sydney / Canberra (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST, no DST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST, no DST)' },
  { value: 'Pacific/Auckland', label: 'Auckland / Wellington (NZST/NZDT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'America/New_York', label: 'New York (ET)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris / Berlin (CET/CEST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
];

// ── Zone resolution ─────────────────────────────────────────────────────────

/** The device's IANA zone, e.g. `Australia/Sydney`. Falls back to `UTC`. */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** True when `zone` is a valid IANA zone Intl accepts. */
export function isValidTimeZone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The effective zone: a valid preference wins, else the device zone. */
export function resolveTimeZone(pref?: string | null): string {
  if (pref && isValidTimeZone(pref)) return pref;
  return deviceTimeZone();
}

/** React hook: the effective viewer zone (profile preference → device zone). */
export function useEffectiveTimeZone(): string {
  return resolveTimeZone(useTzPreference());
}

// ── Formatting ──────────────────────────────────────────────────────────────

/**
 * `day-time` — "30 Oct, 07:34 ACDT": a table's last-seen cell, where the year is noise (Alerts table, 31 October
 * 2026). `day-time-plain` — the same without the zone label, for the "first …" line set beneath it.
 */
export type InstantMode = 'datetime' | 'datetime-sec' | 'date' | 'time' | 'time-sec' | 'compact' | 'day-time' | 'day-time-plain';

export interface FormattedInstant {
  /** The localised, zone-labelled text (label omitted for the `date` mode). */
  text: string;
  /** The short zone label at this instant (e.g. `AEST`, `NZDT`), '' if unknown. */
  label: string;
  /** The raw UTC instant, for the hover title (e.g. `2026-07-11 08:57:00 UTC`). */
  utc: string;
}

/** The short zone label for an instant in a zone (DST-aware: AEST vs AEDT). */
function zoneLabel(date: Date, zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: zone,
      timeZoneName: 'short',
      hour: '2-digit',
    }).formatToParts(date);
    const tz = parts.find((p) => p.type === 'timeZoneName');
    return tz ? tz.value : '';
  } catch {
    return '';
  }
}

function utcTitle(date: Date): string {
  // "2026-07-11 08:57:00 UTC" — unambiguous, matches the ISO the row stores.
  return date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

// A "naive" wall-clock timestamp with a time component and NO zone designator
// (no trailing `Z`, no `+hh:mm`/`-hh:mm` offset) — the shape ClickHouse/Tinybird
// emit, e.g. `2026-07-11 12:15:03` or `2026-07-11T12:15:03.250`. JS `new Date`
// parses this shape as LOCAL time, silently mis-placing a UTC-stored instant by
// the viewer's offset. STORAGE IS UTC EVERYWHERE, so a naive instant IS UTC:
// normalise it to explicit UTC (`T`-separator + `Z`) before parsing. Strings
// that already carry a zone (`…Z`, `…+09:30`), and pure civil dates with no
// time component, do NOT match and pass through untouched.
const NAIVE_INSTANT = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/;

/** Normalise a naive UTC wall-clock string to an explicit-UTC ISO string.
 * Non-naive inputs (zone-bearing ISO, epoch-as-string, civil date, garbage)
 * are returned verbatim. */
function normalizeNaiveUtc(raw: string): string {
  return NAIVE_INSTANT.test(raw) ? raw.replace(' ', 'T') + 'Z' : raw;
}

const MODE_OPTS: Record<InstantMode, Intl.DateTimeFormatOptions> = {
  datetime: { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
  'datetime-sec': { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
  date: { day: '2-digit', month: 'short', year: 'numeric' },
  time: { hour: '2-digit', minute: '2-digit', hour12: false },
  'time-sec': { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
  compact: { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
  'day-time': { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false },
  'day-time-plain': { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false },
};

/**
 * Format an instant (ISO/epoch-parseable string) in a zone. NEVER blank: an
 * unparseable input returns the raw string verbatim (the never-blank fallback
 * the old formatTs guaranteed). `date` mode carries no zone label (no clock);
 * every other mode appends the short zone label.
 */
export function formatInstant(iso: string, zone: string, mode: InstantMode = 'datetime'): FormattedInstant {
  const raw = iso ?? '';
  const date = new Date(normalizeNaiveUtc(raw));
  if (!raw || Number.isNaN(date.getTime())) {
    return { text: raw, label: '', utc: raw };
  }
  const label = mode === 'date' || mode === 'day-time-plain' ? '' : zoneLabel(date, zone);
  let body: string;
  try {
    body = new Intl.DateTimeFormat('en-AU', { timeZone: zone, ...MODE_OPTS[mode] }).format(date);
  } catch {
    body = raw;
  }
  return { text: label ? `${body} ${label}` : body, label, utc: utcTitle(date) };
}

// ── <InstantTime> component ─────────────────────────────────────────────────

export interface InstantTimeProps {
  /** The instant to render — an ISO string, or epoch millis as a string/number. */
  iso: string | number | null | undefined;
  mode?: InstantMode;
  className?: string;
  /** Rendered verbatim when `iso` is empty/nullish (default '—'). */
  empty?: string;
}

/**
 * Render an instant as a `<time>` element in the viewer's zone, labelled with
 * its short zone name and revealing the UTC instant on hover. Drop-in for any
 * `new Date(x).toLocaleString()` display of a real moment in time.
 */
export function InstantTime({ iso, mode = 'datetime', className, empty = '—' }: InstantTimeProps): JSX.Element {
  const zone = useEffectiveTimeZone();
  if (iso === null || iso === undefined || iso === '') {
    return <span className={className}>{empty}</span>;
  }
  const value = typeof iso === 'number' ? new Date(iso).toISOString() : iso;
  const { text, utc } = formatInstant(value, zone, mode);
  return (
    <time className={className} dateTime={value} title={`${utc} (UTC)`}>
      {text}
    </time>
  );
}

// ── Days, clocks and months (27 October 2026 — the portal-wide sweep) ───────

/** The zone in force right now, outside React: the profile preference, else the device zone.
 *  Components use `useEffectiveTimeZone()` so they re-render when the preference changes;
 *  plain helpers default to this. */
export function currentTimeZone(): string {
  return resolveTimeZone(getTzPreference());
}

/** A civil (zone-free) calendar day, `YYYY-MM-DD`. */
export type CivilDate = string;

const CIVIL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a bare `YYYY-MM-DD` — a business day, not an instant. */
export function isCivilDate(value: string): boolean {
  return CIVIL_DATE.test(value);
}

/** An instant from an ISO string (naive ⇒ UTC, the Time Standard) or epoch millis. Invalid ⇒ NaN date. */
export function parseInstant(value: string | number): Date {
  return new Date(typeof value === 'number' ? value : normalizeNaiveUtc(value));
}

const dayFormatters = new Map<string, Intl.DateTimeFormat>();
function dayFormatter(zone: string): Intl.DateTimeFormat {
  let f = dayFormatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
    dayFormatters.set(zone, f);
  }
  return f;
}

/** The calendar day an instant falls on in `zone`, as `YYYY-MM-DD`. Null when unparseable. */
export function civilDayOf(value: string | number | Date, zone: string = currentTimeZone()): CivilDate | null {
  if (typeof value === 'string' && isCivilDate(value)) return value;
  const d = value instanceof Date ? value : parseInstant(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return dayFormatter(zone).format(d);
  } catch {
    return dayFormatter('UTC').format(d);
  }
}

/** Today in `zone`, as `YYYY-MM-DD`. */
export function todayIn(zone: string = currentTimeZone(), now: Date | number = Date.now()): CivilDate {
  return civilDayOf(now instanceof Date ? now : new Date(now), zone)!;
}

interface CivilParts { y: number; m: number; d: number }
function partsOf(day: CivilDate): CivilParts {
  const m = CIVIL_DATE.exec(day)!;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}
function civilFromUtc(t: Date): CivilDate {
  return t.toISOString().slice(0, 10);
}

/** `day` plus `n` calendar days (civil arithmetic — no DST hour can skew it). */
export function addCivilDays(day: CivilDate, n: number): CivilDate {
  const p = partsOf(day);
  return civilFromUtc(new Date(Date.UTC(p.y, p.m - 1, p.d + n)));
}

/** The 1st of the month `n` months after `day`'s month. */
export function firstOfMonth(day: CivilDate, n = 0): CivilDate {
  const p = partsOf(day);
  return civilFromUtc(new Date(Date.UTC(p.y, p.m - 1 + n, 1)));
}

/** `day` with its month moved by `n` (day-of-month clamped by the calendar, as `Date` does). */
export function addCivilMonths(day: CivilDate, n: number): CivilDate {
  const p = partsOf(day);
  return civilFromUtc(new Date(Date.UTC(p.y, p.m - 1 + n, p.d)));
}

/** Day of the month (1–31) of a civil day. */
export function dayOfMonth(day: CivilDate): number {
  return partsOf(day).d;
}

/** English month names, January first. */
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * How a day is written:
 *   - `short`      "25 Oct"            capsules, separators
 *   - `long`       "1 December"        prose due dates
 *   - `medium`     "7 Sep 2026"        tables, billing (org date standard: full day date)
 *   - `full`       "15 October 2026"   pills and headings
 *   - `month-year` "November 2026"
 *   - `month`      "November"
 *   - `numeric`    "11/06/26"          the compact pill strip
 */
export type DayStyle = 'short' | 'long' | 'medium' | 'full' | 'month-year' | 'month' | 'numeric';

function writeDay(p: CivilParts, style: DayStyle): string {
  const name = MONTH_NAMES[p.m - 1];
  switch (style) {
    case 'short': return `${p.d} ${name.slice(0, 3)}`;
    case 'long': return `${p.d} ${name}`;
    case 'medium': return `${p.d} ${name.slice(0, 3)} ${p.y}`;
    case 'full': return `${p.d} ${name} ${p.y}`;
    case 'month-year': return `${name} ${p.y}`;
    case 'month': return name;
    case 'numeric': return `${String(p.d).padStart(2, '0')}/${String(p.m).padStart(2, '0')}/${String(p.y).slice(2)}`;
    default: return `${p.d} ${name.slice(0, 3)} ${p.y}`;
  }
}

/**
 * Write a day. A civil `YYYY-MM-DD` renders as stored; an instant renders as the day it falls on
 * in `zone` (default: the viewer's). Never throws; `fallback` for empty or unparseable input.
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  style: DayStyle = 'medium',
  zone: string = currentTimeZone(),
  fallback = '—',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const day = civilDayOf(value, zone);
  return day ? writeDay(partsOf(day), style) : fallback;
}

/** A `YYYY-MM` month written as "November 2026" (or "November"); unparseable input verbatim. */
export function formatMonth(yearMonth: string, style: 'month-year' | 'month' = 'month-year'): string {
  const m = /^(\d{4})-(\d{2})/.exec(yearMonth);
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return yearMonth;
  return writeDay({ y: Number(m[1]), m: Number(m[2]), d: 1 }, style);
}

/** "Today" · "Yesterday" · "25 Oct" — the calendar day of an instant in `zone`, relative to now in `zone`. */
export function calendarDayLabel(value: string | number | Date, zone: string = currentTimeZone(), now: Date | number = Date.now()): string {
  const day = civilDayOf(value, zone);
  if (!day) return '';
  const today = todayIn(zone, now);
  if (day === today) return 'Today';
  if (day === addCivilDays(today, -1)) return 'Yesterday';
  return writeDay(partsOf(day), 'short');
}

/** Whole calendar days from `from` to `to` (civil days; positive when `to` is later). */
export function civilDaysBetween(from: CivilDate, to: CivilDate): number {
  const a = partsOf(from); const b = partsOf(to);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** "7:37:03 am" (or "7:37 am") — an instant's clock in `zone`. Unparseable input verbatim. */
export function formatClock(
  value: string | number | Date,
  zone: string = currentTimeZone(),
  opts: { seconds?: boolean } = {},
): string {
  const d = value instanceof Date ? value : parseInstant(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const seconds = opts.seconds ?? true;
  try {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: zone, hour: 'numeric', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}), hour12: true,
    }).format(d).replace(/\s/g, ' ').toLowerCase();
  } catch {
    return d.toISOString();
  }
}

/**
 * "08:57" (or "08:57:03") — an instant's 24-hour clock in `zone`, with NO zone label. Staff-only
 * (ops tables and charts write the 24-hour clock): for chart ticks and dense strips where a label on
 * every value is noise and the zone is stated once nearby. Unparseable input verbatim.
 */
export function formatClock24(
  value: string | number | Date,
  zone: string = currentTimeZone(),
  opts: { seconds?: boolean } = {},
): string {
  const d = value instanceof Date ? value : parseInstant(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zone, hour: '2-digit', minute: '2-digit', ...(opts.seconds ? { second: '2-digit' } : {}), hourCycle: 'h23',
    }).format(d);
  } catch {
    return d.toISOString().slice(11, opts.seconds ? 19 : 16);
  }
}

/**
 * "2026-10-30T09:14" — an instant as a `datetime-local` input value, on `zone`'s wall clock. Staff-only:
 * the input has no zone of its own, so its value is written (and read back by `instantFromWallClock`)
 * in the staff member's zone, never the device's or UTC. Null when unparseable.
 */
export function wallClockOf(value: string | number | Date, zone: string = currentTimeZone()): string | null {
  const day = civilDayOf(value, zone);
  if (!day) return null;
  return `${day}T${formatClock24(value, zone)}`;
}

/**
 * The instant a `datetime-local` value ("2026-10-30T09:14") names on `zone`'s wall clock — the inverse of
 * `wallClockOf`. Null when the value is not `YYYY-MM-DDTHH:mm`. A wall time a DST change skips resolves
 * to the instant one offset later, as the platform's own clocks do.
 */
export function instantFromWallClock(wall: string, zone: string = currentTimeZone()): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall);
  if (!m) return null;
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  // The zone's offset at a moment, from its own wall clock read back as UTC.
  const offsetAt = (t: number): number => {
    const w = wallClockOf(t, zone);
    if (!w) return 0;
    const [d, c] = w.split('T');
    const [y, mo, da] = d.split('-').map(Number);
    const [h, mi] = c.split(':').map(Number);
    return Date.UTC(y, mo - 1, da, h, mi) - Math.floor(t / 60_000) * 60_000;
  };
  let t = asUtc - offsetAt(asUtc);
  t = asUtc - offsetAt(t);
  return new Date(t);
}

/** The calendar month before today in `zone`, written "October 2026". */
export function previousMonthLabel(zone: string = currentTimeZone(), now: Date | number = Date.now()): string {
  return formatDate(firstOfMonth(todayIn(zone, now), -1), 'month-year', zone);
}
